import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Alert, Card, Segmented, Tag } from "antd";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { errorText, listInvoices } from "./api";
import { formatDay, formatEuro } from "../../../serviceProvider/components/workReports/format";
import { paymentState } from "./labels";
import "./portal.css";

const OPEN_STATES = ["open", "partially_paid", "overdue"];

/** The customer's invoices with what is still open. */
const PortalInvoices = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const history = useHistory();
  const [state, setState] = useState({ loading: true, list: [] });
  const [filter, setFilter] = useState("open");

  useEffect(() => {
    listInvoices()
      .then(res => setState({ loading: false, list: res.data.data }))
      .catch(error =>
        setState({
          loading: false,
          list: [],
          error: errorText(error, t("The invoices could not be loaded."))
        })
      );
  }, [t]);

  const openCount = state.list.filter(i => OPEN_STATES.includes(i.paymentState)).length;
  const shown = useMemo(
    () =>
      filter === "open" ? state.list.filter(i => OPEN_STATES.includes(i.paymentState)) : state.list,
    [filter, state.list]
  );

  const columns = [
    {
      title: t("Invoice"),
      dataIndex: "number",
      mobile: "title",
      render: (value, record) => (
        <Link to={`/client/invoices/${record._id}`}>
          {[value, record.title].filter(Boolean).join(" – ")}
        </Link>
      )
    },
    { title: t("Date"), dataIndex: "issueDate", render: value => formatDay(value) },
    {
      title: t("Due"),
      dataIndex: "dueDate",
      mobile: "subtitle",
      render: value => (value ? formatDay(value) : "–")
    },
    {
      title: t("Status"),
      dataIndex: "paymentState",
      mobile: "badge",
      render: value => {
        const tag = paymentState(t, value);
        return <Tag color={tag.color}>{tag.text}</Tag>;
      }
    },
    {
      title: t("Amount"),
      dataIndex: "grossCents",
      align: "right",
      render: value => (value === null ? "–" : formatEuro(value, lng))
    },
    {
      title: t("Open"),
      dataIndex: "openCents",
      align: "right",
      render: (value, record) =>
        OPEN_STATES.includes(record.paymentState) ? formatEuro(value, lng) : "–"
    }
  ];

  return (
    <div className="portal-page">
      {state.error && <Alert className="portal-gap" type="error" showIcon message={state.error} />}
      <Card title={t("Invoices")} className="portal-card">
        <Segmented
          className="portal-gap"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "open", label: `${t("Open")} (${openCount})` },
            { value: "all", label: t("All") }
          ]}
        />
        <ResponsiveTable
          rowKey="_id"
          loading={state.loading}
          columns={columns}
          dataSource={shown}
          pagination={shown.length > 20 ? { pageSize: 20 } : false}
          mobileAvatar={false}
          onRow={record => ({ onClick: () => history.push(`/client/invoices/${record._id}`) })}
          locale={{ emptyText: filter === "open" ? t("No open invoices.") : t("No invoices yet.") }}
        />
      </Card>
    </div>
  );
};

export default PortalInvoices;
